import type { AuthProviders } from '@/lib/api';

/** A single `/auth/me` provider entry, paired with the name the API knows it by. */
export type AuthProviderEntry = [name: string, provider: AuthProviders[string]];

/**
 * The providers a sign-in surface should offer, split by how they render.
 *
 * `oauth` entries become redirect buttons; `password` entries become an
 * inline email/password form contributed by the email-auth plugin. Surfaces
 * that cannot host a form (the invite landing page) offer only `oauth`.
 */
export interface SignInProviders {
  oauth: AuthProviderEntry[];
  password: AuthProviderEntry[];
}

/**
 * Split the advertised auth providers into the redirect and inline-form
 * groups, dropping the ones an operator hid from the sign-in screens.
 *
 * Hiding is a service-level toggle per provider (`HIDE_ON_LOGIN_KEY`);
 * `showHidden` is the visitor's reveal opt-in, which brings them back. Every
 * surface that offers sign-in goes through this helper, so the split and the
 * hide toggle have one implementation and a provider suppressed on one page
 * cannot reappear on another.
 *
 * Callers pass `showHidden: true` only when the surface deliberately offers
 * everything regardless of the toggle — first-run setup, where there is no
 * operator yet to have made the choice.
 */
export function selectSignInProviders(
  providers: AuthProviders,
  showHidden: boolean,
): SignInProviders {
  const visible = Object.entries(providers).filter(([, p]) => showHidden || !p.hidden);
  return {
    oauth: visible.filter(([, p]) => p.kind !== 'password'),
    password: visible.filter(([, p]) => p.kind === 'password'),
  };
}
